import { useCallback, useEffect, useRef } from "react";

/**
 * ダブルクリック（= 決定）と見なす 2 打目までの猶予(ms)。
 * OS の一般的なダブルクリック判定に合わせた値。
 */
const DOUBLE_CLICK_WINDOW_MS = 300;

export interface DeferredReclick {
  /**
   * 「同じカードの再クリック」による二次アクションを予約する。
   * 猶予内に {@link DeferredReclick.cancel} が呼ばれたら実行しない。
   */
  // eslint-disable-next-line no-unused-vars
  schedule: (action: () => void) => void;
  /** 予約済みの二次アクションを取り消す。 */
  cancel: () => void;
}

/**
 * 同じカード上で「再クリック = 二次アクション」と「ダブルクリック = 決定」を
 * 共存させるためのフック。
 *
 * オファー作成ウィザードのカードは、シングルクリックが選択、ダブルクリックが
 * 「次へ」、そして選択済みカードの再クリックが二次アクション（ロール = 日本語 /
 * クレジット表記のトグル、メール招待 = アドレス編集モーダル）になっている。
 * 素直に実装すると「決定のつもりのダブルクリック」の 1 打目で二次アクションが
 * 走ってしまい、表記が裏返ったりモーダルが一瞬開いたりする。
 *
 * そこで二次アクションを {@link DOUBLE_CLICK_WINDOW_MS} だけ遅延実行し、その間に
 * ダブルクリックが来たら取り消す。単独の再クリックはわずかに遅れて実行される。
 */
export const useDeferredReclick = (): DeferredReclick => {
  const timerRef = useRef<number | null>(null);

  const cancel = useCallback(() => {
    if (timerRef.current === null) return;
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const schedule = useCallback(
    (action: () => void) => {
      cancel();
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        action();
      }, DOUBLE_CLICK_WINDOW_MS);
    },
    [cancel],
  );

  useEffect(() => cancel, [cancel]);

  return { schedule, cancel };
};
